// © Microsoft Corporation. All rights reserved.

import { FASTElement, observable } from "@microsoft/fast-element";
import { StartEnd, applyMixins } from "@microsoft/fast-foundation";
import { keyCodeEnter, keyCodeSpace } from "@microsoft/fast-web-utilities";

import { throttle } from "lodash-es";

export interface NavigationItem {
    id: string;
    display: string;
    glyph?: string;
    isActive?: boolean;
    href?: string;
    showPipeSeparator?: boolean;
    telemetryMetadata?: { [index: string]: string };
}

export interface LocalizedStrings {
    overflowButtonTooltip: string;
}

export class NavBar extends FASTElement {
    /** Nav data, received as input from caller */
    @observable public data: NavigationItem[];

    /** Localized strings used for tool tip and aria-label */
    @observable public localizedStrings: LocalizedStrings = {} as LocalizedStrings;

    /** Currently active nav item Id */
    @observable public activeId: string | null;

    /** Flag to show/hide overflow button  */
    @observable public isOverflowing = false;

    /** Flag to show/hide overflow menu */
    @observable public showMenu = false;

    /** Items overflowing horizontal nav, these will be shown in overflow menu */
    @observable public overflowItems: NavigationItem[] = [];

    /** Telemetry tag assigned to the overflow button */
    @observable public overflowTelemetryTag: string;

    /** Horizontal nav container element reference */
    public navContainer: HTMLElement;

    public overflowButton: HTMLElement;

    /** ResizeObserver instance that watches for horizontal nav container resize event */
    private resizeObserver = new window.ResizeObserver(
        throttle(this.onContainerResize.bind(this), 200)
    );

    /**
     * Connected callback
     */
    public connectedCallback(): void {
        super.connectedCallback();
        this.resizeObserver.observe(this.navContainer);
    }

    /**
     * Disconnected callback
     */
    public disconnectedCallback(): void {
        super.disconnectedCallback();
        this.resizeObserver.disconnect();
    }

    /**
     * Data change subscription
     * @param oldValue - old value
     * @param newValue - new value
     */
    private dataChanged(oldValue: NavigationItem[], newValue: NavigationItem[]): void {
        if (!newValue || !newValue.length) {
            return;
        }

        this.activeId = null;

        newValue.forEach((item, index) => {
            item.id = item.id || `nav-${index}`;
            if (!this.activeId && item.isActive === true) {
                this.activeId = item.id;
            }
        });

        if (oldValue) {
            requestAnimationFrame(() => {
                this.onContainerResize();
                if (this.activeId) {
                    const activeElement = this.navContainer.querySelector(
                        `#${this.activeId}`
                    ) as HTMLElement;
                }
            });
        }
    }

    /** Nav item click handler */
    public onNavItemClick = (selectedNav: NavigationItem): void => {
        const itemId = selectedNav.id;
        this.activeId = itemId;
        this.change(selectedNav);
        this.showMenu = false;
    };

    public onNavItemKeyPress = (
        event: KeyboardEvent,
        selectedNav: NavigationItem
    ): void => {
        const { keyCode } = event;

        if (keyCode !== keyCodeEnter && keyCode !== keyCodeSpace) {
            return;
        }

        this.onNavItemClick(selectedNav);
        event.preventDefault();
    };

    /** Overflow menu button click handler  */
    public toggleOverflowMenu = () => {
        if (this.showMenu === true) {
            this.hideOverflowMenu();
        } else {
            this.showOverflowMenu();
        }
    };

    private showOverflowMenu() {
        this.showMenu = true;
        document.addEventListener("click", this.onClickOutside);
        document.addEventListener("scroll", this.onScrollOutside);
        document.addEventListener("keydown", this.onKeyDown);
    }

    private hideOverflowMenu() {
        this.showMenu = false;
        document.removeEventListener("click", this.onClickOutside);
        document.removeEventListener("scroll", this.onScrollOutside);
        document.removeEventListener("keydown", this.onKeyDown);
    }

    private change = (navItem: NavigationItem): void => {
        this.$emit("change", { selectedNav: navItem });
    };

    private onContainerResize(): void {
        this.overflowItems = [];
        this.showMenu = false;

        let width = 0;
        const containerWidth = this.navContainer.offsetWidth;

        Array.from(this.navContainer.children).forEach(
            (navElement: HTMLElement, index: number) => {
                const navItem = this.data[index];
                if (!navItem) {
                    return;
                }

                width += navElement.offsetWidth;

                if (width > containerWidth) {
                    this.overflowItems.push(navItem);
                    navElement.classList.add("hidden");
                } else {
                    navElement.classList.remove("hidden");
                }
            }
        );

        this.isOverflowing = !!this.overflowItems.length;
    }

    private onClickOutside = (event: MouseEvent): void => {
        if (event.target instanceof NavBar) {
            return;
        }

        const path = event.composedPath();
        if (path && path[0] === this.overflowButton) {
            return;
        }

        this.hideOverflowMenu();
    };

    private onScrollOutside = throttle((): void => {
        this.hideOverflowMenu();
    }, 200);

    private onKeyDown = (event: KeyboardEvent) => {
        const { code } = event;

        if (code !== "Escape") {
            return;
        }

        this.hideOverflowMenu();
    };
}

export interface NavBar extends StartEnd { }
applyMixins(NavBar, StartEnd);
